import { Injectable } from '@angular/core';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { AgGridApiBusService } from '../ag-grid-api-bus/ag-grid-api-bus.service';
import { map, switchMap } from 'rxjs/operators';
import { AgGridAngular } from '@ag-grid-community/angular';
import { ColDef, GridApi, RowDataUpdatedEvent } from '@ag-grid-community/core';
import { RowNode } from '@ag-grid-community/core/dist/cjs/es5/entities/rowNode';

@Injectable()
export class CommonGridAgGridApiService {
    public readonly agGridApi: Promise<GridApi>;
    public readonly rowDataUpdated$: Observable<RowDataUpdatedEvent<unknown>>;

    constructor(private agGridApiBusService: AgGridApiBusService) {
        this.agGridApi = firstValueFrom(
            this.agGridApiBusService.agGridComponent.pipe(
                switchMap((agGridApi: AgGridAngular) => combineLatest([agGridApi.firstDataRendered, agGridApi.gridReady])),
                map(([, gridOptions]) => gridOptions.api)
            )
        );

        this.rowDataUpdated$ = this.agGridApiBusService.agGridComponent.pipe(
            switchMap((agGridApi: AgGridAngular) => agGridApi.rowDataUpdated)
        );
    }

    public async getSelectedRows(): Promise<RowNode[]> {
        return (await this.agGridApi).getSelectedNodes();
    }

    public async getSelectedRowsIds(): Promise<string[]> {
        const selectedRows = await this.getSelectedRows();
        return selectedRows.map((selectedRow) => selectedRow.id).filter((id): id is string => id !== undefined);
    }

    public selectRowsInGrid(rowsIds: string[]): Promise<void> {
        return Promise.all(rowsIds.map((selectedRowNodeId) => this.selectRow(selectedRowNodeId))).then(() => void 0);
    }

    public async safeExecute<FnName extends keyof GridApi>(functionName: FnName, ...args: Parameters<GridApi[FnName]>): Promise<void> {
        const agGridApi = await this.agGridApi;
        const fn = agGridApi[functionName] as unknown as (...fnArgs: Parameters<GridApi[FnName]>) => unknown;
        fn.apply(agGridApi, args);
    }

    public async selectFirstRow(columnDefs: ColDef[]): Promise<void> {
        const groupLinesCountInTopOfGrid = this.getCountOfGroupingRows(columnDefs);
        await this.safeExecute('expandAll');
        await (await this.agGridApi).getDisplayedRowAtIndex(groupLinesCountInTopOfGrid)?.setSelected(true);
    }

    public async safeExecuteOnRow<FnName extends keyof RowNode>(
        rowId: string,
        functionName: FnName,
        ...args: Parameters<RowNode[FnName]>
    ): Promise<void> {
        const rowNode = (await this.agGridApi).getRowNode(rowId);
        if (rowNode) {
            const fn = rowNode[functionName] as unknown as (...fnArgs: Parameters<RowNode[FnName]>) => unknown;
            fn.apply(rowNode, args);
        }
    }

    private getCountOfGroupingRows(columnDefs: ColDef[]): number {
        return columnDefs.filter((column) => column.rowGroup || column.rowGroupIndex).length;
    }

    private async selectRow(selectedRowNodeId: string): Promise<void> {
        (await this.agGridApi).getRowNode(selectedRowNodeId)?.setSelected(true);
    }
}
